'use client';

import { Checkbox, Switch } from 'radix-ui';
import type { ReactNode } from 'react';
import { cx } from './cx';

type CheckboxProps = {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Подпись справа. Клик по ней тоже переключает — обёртка <label>. */
  label?: ReactNode;
  /** Пояснение под подписью. Видимо всегда (правило кабинета), не под значком «i». */
  hint?: ReactNode;
  ariaLabel?: string;
  className?: string;
};

/**
 * Галочка с подписью. Вид — КРУЖОК-ОТМЕТКА iOS (`ScrPickMark`/`Aud.tick`: круг 28 заливкой
 * accent, белая галочка; пусто — круг обводкой), ТЗ WEB-LOOK Э4.4: квадратов больше нет.
 * Классы: `.k-mark` — вид (общий с витриной и отбором нескольких), `.k-check` — прежний
 * квадрат для iOS-приложения (блок `:where(html.app) .k-check` в kit.css, ТЗ 1.6).
 */
export function KCheckbox({ checked, onChange, disabled, label, hint, ariaLabel, className }: CheckboxProps) {
  return (
    <label className={cx('k k-row', disabled && 'is-off', className)}>
      <Checkbox.Root
        checked={checked}
        onCheckedChange={(v) => onChange?.(v === true)}
        disabled={disabled}
        aria-label={label ? undefined : ariaLabel}
        className="k-ctl k-mark k-check"
      >
        <Checkbox.Indicator className="k-check__tick">
          <svg width="12" height="12" viewBox="0 0 13 13" fill="none" aria-hidden="true">
            <path d="M2.5 7 5 9.5l5.5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Checkbox.Indicator>
      </Checkbox.Root>
      {label || hint ? (
        <span className="k-row__text">
          {label ? <span className="k-row__label">{label}</span> : null}
          {hint ? <span className="k-row__hint">{hint}</span> : null}
        </span>
      ) : null}
    </label>
  );
}

type SwitchProps = Omit<CheckboxProps, 'label' | 'hint'> & {
  label?: ReactNode;
  hint?: ReactNode;
};

/** Переключатель. Вид — iOS-пилюля, как в настройках кабинета. */
export function KSwitch({ checked, onChange, disabled, label, hint, ariaLabel, className }: SwitchProps) {
  // Структура владельца (2026-08-11): строка «имя ЖИРНЫМ + переключатель СПРАВА»,
  // описание — ПОД строкой на всю ширину (описание любого элемента живёт снизу).
  return (
    <label className={cx('k k-row k-row--sw', disabled && 'is-off', className)}>
      <span className="k-row__top">
        {label ? <span className="k-row__label">{label}</span> : null}
        <Switch.Root
          checked={checked}
          onCheckedChange={(v) => onChange?.(v)}
          disabled={disabled}
          // Заперт — и для чтения с экрана, и для проверок (`aria-disabled`, PAY-FLOW-TZ §16 К11); вид — общий
          // `.k-ctl[aria-disabled='true']`.
          aria-disabled={disabled || undefined}
          aria-label={label ? undefined : ariaLabel}
          className="k-ctl k-sw"
        >
          <Switch.Thumb className="k-sw__dot" />
        </Switch.Root>
      </span>
      {hint ? <span className="k-row__hint">{hint}</span> : null}
    </label>
  );
}
